import React, { useEffect, useState } from "react";
import { Footer, Navbar } from "../components";
import { useSelector, useDispatch } from "react-redux";
import { addCart, delCart, setCart } from "../redux/action";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api from "../utils/base_url";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import { mapReservedToCartItems, verifyCartOffersAndPrices } from "../redux/reducer/Carthelpers";
import '../styles/index.css';
import "../styles/Cart.css";

// Build the {product|combo, quantity} line the preview-amount API expects.
const toPreviewLine = (item) =>
  item.itemType === "combo"
    ? { combo: item.refId, quantity: item.qty }
    : { product: item.refId, quantity: item.qty };

// Same-shape fallback used ONLY for the very first paint / while the
// preview request is in flight, so there's no flicker. The number that
// actually matters — the one used at checkout/Razorpay — always comes
// from POST preview-amount/ below.
const computeFallbackAmount = (items = []) =>
  items.reduce((acc, item) => {
    const priceToUse =
      item.offer_price && Number(item.offer_price) > 0
        ? Number(item.offer_price)
        : Number(item.price);
    return acc + priceToUse * item.qty;
  }, 0);

const ShowCart = ({
  state,
  stocks,
  stockError,
  disabledQty,
  addItem,
  removeItem,
  formatName,
  initiateCheckout,
  navigate
}) => {
  // 🔒 Backend-authoritative product amount. Covers normal price, active
  // offer discount, and combo price — the exact same price_cart() used to
  // create the Razorpay order, so this can never drift from what gets
  // charged at checkout.
  const [productAmount, setProductAmount] = useState(() => computeFallbackAmount(state));

  useEffect(() => {
    // Instant, familiar number on every render (no loading UI change)…
    setProductAmount(computeFallbackAmount(state));

    // …then reconcile with the backend the moment it responds.
    let cancelled = false;
    const fetchPreview = async () => {
      if (!state.length) return;
      try {
        const res = await api.post("preview-amount/", {
          items: state.map(toPreviewLine),
        });
        if (!cancelled && res.data.status) {
          const d = res.data.data;
          setProductAmount(Number(d.subtotal) + Number(d.tax));
        }
      } catch (err) {
        console.error("Preview amount fetch error:", err);
      }
    };
    fetchPreview();
    return () => { cancelled = true; };
  }, [state]);

  const subtotal = productAmount;

  const MIN_ORDER_VALUE = 500;

  return (
    <section className="h-100 gradient-custom-themed">
      <div className="container py-5">
        <div className="row d-flex justify-content-center my-4">

          {/* Cart items */}
          <div className="col-lg-8 col-md-10 col-12 mb-4">
            <div className="card mb-4 rounded-theme shadow-theme">
              <div className="card-header py-3 cart-card-header">
                <h5 className="mb-0 cart-card-title">
                  Item List
                </h5>
              </div>
              <div className="card-body p-3">
                {state.map((item) => {
                  const priceToUse =
                    item.offer_price && Number(item.offer_price) > 0
                      ? Number(item.offer_price)
                      : Number(item.price);

                  // Strikethrough price: either a product's original price
                  // (when an offer applies) or a combo's "you save" price.
                  const originalPrice =
                    item.offer_price && Number(item.offer_price) > 0
                      ? Number(item.price)
                      : item.original_price && Number(item.original_price) > Number(item.price)
                        ? Number(item.original_price)
                        : null;

                  return (
                    <div
                      key={item.id}
                      className="mb-3 pb-3 border-bottom border-theme-light cart-clickable-item"
                      onClick={() =>
                        navigate(item.itemType === "combo" ? `/combo/${item.refId}` : `/product/${item.refId}`)
                      }
                    >

                      <div className="row align-items-center g-2">
                        <div className="col-4 col-sm-3 text-center">
                          <img
                            src={`${item.product_image}`}
                            alt={formatName(item.product_name)}
                            className="cart-product-image rounded-theme img-fluid"
                          />
                        </div>

                        <div className="col-8 col-sm-5">
                          <p className="cart-product-title" >
                            {formatName(item.product_name)}
                          </p>

                          {item.quantity && item.quantity_unit && (
                            <p className="cart-qty-unit">
                              {["size", "color"].includes(
                                item.quantity_unit?.toLowerCase()
                              )
                                ? item.quantity
                                : `${item.quantity} ${item.quantity_unit}`}
                            </p>
                          )}
                          <p className="cart-price-text">
                            ₹ {priceToUse.toLocaleString()}{" "}
                            {originalPrice && (
                              <span className="cart-original-price">
                                ₹ {originalPrice.toLocaleString()}
                              </span>
                            )}
                          </p>
                        </div>

                        <div className="col-12 col-sm-4 d-flex align-items-center justify-content-between justify-content-sm-end gap-2">
                          <div className="input-group input-group-sm quantity-controls">
                            {item.qty > 1 ? (
                              <button
                                className="btn btn-themed-outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeItem(item);
                                }}
                                aria-label={`Remove one ${item.product_name}`}
                              >
                                <i className="fas fa-minus"></i>
                              </button>
                            ) : (
                              <button
                                className="btn btn-themed-outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeItem(item);
                                }}
                                aria-label={`Remove ${item.product_name} from cart`}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            )}

                            <span className="input-group-text qty-display">
                              {item.qty}
                            </span>
                            <button
                              className="btn btn-themed-outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                addItem(item);
                              }}
                              disabled={disabledQty[item.id]}
                            >
                              <i className="fas fa-plus"></i>
                            </button>
                          </div>

                          <p
                            className="cart-total-price"
                          >
                            ₹ {(priceToUse * item.qty).toLocaleString()}
                          </p>

                          {stockError[item.id] && (
                            <motion.span
                              initial={{ scale: 0.8, opacity: 0 }}
                              animate={{ scale: [0.8, 1.05, 1], opacity: 1 }}
                              transition={{ duration: 0.3, ease: "easeOut" }}
                              className="stock-error-badge"
                            >
                              Stock limit reached!
                            </motion.span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="col-lg-4 col-md-8 col-12 mb-4">
            <div className="card rounded-theme shadow-theme">
              <div className="card-header py-3 cart-card-header">
                <h5 className="mb-0 cart-card-title">
                  Order Summary
                </h5>
              </div>
              <div className="card-body p-3">
                <ul className="list-group list-group-flush mb-3">

                  {/* Product subtotal */}
                  <li className="list-group-item d-flex justify-content-between px-0">
                    Product Amount
                    <span>₹{Math.round(subtotal).toLocaleString()}</span>
                  </li>

                  {/* Courier charge — informational text only, no value */}
                  <li className="list-group-item d-flex justify-content-between px-0 align-items-center">
                    <span>Courier Charge</span>
                    <span className="courier-note">
                      calculated at checkout
                    </span>
                  </li>

                  {/* Total — product amount only, courier added at checkout */}
                  <li className="list-group-item d-flex justify-content-between px-0 fw-bold align-items-center">
                    <span>Sub Total</span>
                    <span>
                      ₹{Math.round(subtotal).toLocaleString()}{" "}
                      <span className="subtotal-note">
                        + courier
                      </span>
                    </span>
                  </li>
                </ul>

                <button
                  className="btn-themed btn-lg w-100 text-center"
                  onClick={initiateCheckout}
                  disabled={subtotal < MIN_ORDER_VALUE}
                >
                  {subtotal < MIN_ORDER_VALUE
                    ? `Add ₹${(
                      MIN_ORDER_VALUE - subtotal
                    ).toLocaleString()} more`
                    : "Go to checkout"}
                </button>

                {subtotal < MIN_ORDER_VALUE && (
                  <p className="minimum-order-warning">
                    Minimum order value is ₹500
                  </p>
                )}

                {/* Helper note below button */}
                <p className="delivery-helper-text">
                  Delivery charge will be added based on your pincode
                </p>

              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

const Cart = () => {

  const [stocks, setStocks] = useState({});
  const [stockError, setStockError] = useState({});
  const [disabledQty, setDisabledQty] = useState({});

  const state = useSelector((state) => state.handleCart);
  const dispatch = useDispatch();

  const { user } = useAuth();
  const location = useLocation();

  const addItem = (item) => {
    const availableStock = stocks[item.id];
    const nextQty = item.qty + 1;

    if (availableStock !== undefined && nextQty > availableStock) {
      setStockError(prev => ({ ...prev, [item.id]: true }));
      setDisabledQty(prev => ({ ...prev, [item.id]: true }));

      setTimeout(() => {
        setStockError(prev => ({ ...prev, [item.id]: false }));
        setDisabledQty(prev => ({ ...prev, [item.id]: false }));
      }, 2000);

      return;
    }

    dispatch(addCart(item));
  };

  const navigate = useNavigate();

  const initiateCheckout = async () => {
    const token = sessionStorage.getItem("access");
    if (!token) {
      sessionStorage.setItem(
        "redirect_toast",
        "Please login first to continue checkout"
      );

      sessionStorage.setItem(
        "login_redirect",
        JSON.stringify({
          pathname: location.pathname,
          search: location.search,
          state: location.state,
        })
      );

      return navigate("/login");
    }

    // 1. Verify live offer/price/stock updates right at the moment checkout is clicked
    const { hasChanges, updatedCart, changeReason } = await verifyCartOffersAndPrices(state, api);

    if (hasChanges) {
      dispatch(setCart(updatedCart));
      toast.error(
        changeReason
          ? `${changeReason} Cart updated — please review your items before checkout.`
          : "Cart prices or offer status changed. Cart updated — please review before checkout."
      );
      return; // Block checkout navigation!
    }

    const userId = user.id;
    const cartPayload = updatedCart.map((item) =>
      item.itemType === "combo"
        ? { combo: item.refId, quantity: item.qty }
        : { product: item.refId, quantity: item.qty }
    );

    try {
      const res = await api.post("checkout-initiate/", {
        user_id: userId,
        cart: cartPayload,
      });

      if (!res.data.status) {
        toast.error(
          res.data.message ||
          res.data.error ||
          "Cart updated due to stock or offer changes. Please review and click checkout again."
        );

        if (res.data.reserved_items) {
          const final = mapReservedToCartItems(res.data.reserved_items);
          dispatch(setCart(final));
        }
        return; // Block checkout navigation!
      }

      if (res.data.reserved_items && Array.isArray(res.data.reserved_items) && res.data.reserved_items.length > 0) {
        const final = mapReservedToCartItems(res.data.reserved_items);
        dispatch(setCart(final));
      }

      navigate("/checkout");
    } catch (err) {
      console.error("initiateCheckout error:", err);
      const backendMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.response?.data?.detail ||
        (typeof err.response?.data === "string" ? err.response?.data : null);

      toast.error(
        backendMsg || "Unable to start checkout due to offer or price changes. Please review your cart items."
      );
      if (err.response?.data?.reserved_items) {
        const final = mapReservedToCartItems(err.response.data.reserved_items);
        dispatch(setCart(final));
      }
    }
  };

  const formatName = (text) => {
    if (!text) return "";
    const cleaned = text.replace(/_/g, " ").toLowerCase();
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  };

  const removeItem = (item) => {
    dispatch(delCart(item));
    setStockError(prev => ({ ...prev, [item.id]: false }));
  };

  const EmptyCart = () => (
    <div className="empty-cart-wrapper">
      <div className="empty-cart-card">

        <div className="empty-cart-icon">
          <i className="fas fa-shopping-bag"></i>
        </div>

        <h4 className="empty-cart-title">
          Your Cart is Empty
        </h4>

        <p className="empty-cart-subtitle">
          Looks like you haven’t added anything yet.
        </p>

        <Link
          to="/product"
          className="empty-cart-btn"
        >
          <i className="fa fa-arrow-left"></i>
          Continue Shopping
        </Link>
      </div>
    </div>
  );


  // ── Sync cart items with fresh backend data (offers, price changes, stock availability)
  useEffect(() => {
    let active = true;

    const syncCartWithLatestData = async () => {
      if (!state.length) return;

      try {
        const updatedCart = await Promise.all(
          state.map(async (item) => {
            if (item.itemType === "combo") {
              try {
                const res = await api.get(`combolist/${item.refId}/`);
                const data = res.data?.data;
                if (!data) return item;

                const newPrice = Number(data.combo_price ?? item.price);
                const newOrigPrice = data.original_price ? Number(data.original_price) : item.original_price;
                const newStock = Number(data.available_stock ?? data.stock_quantity ?? item.stock_quantity ?? 0);

                return {
                  ...item,
                  price: newPrice,
                  original_price: newOrigPrice,
                  stock_quantity: newStock,
                  available_stock: newStock,
                  is_available: newStock > 0 && data.is_available !== false,
                };
              } catch (err) {
                return item;
              }
            } else {
              try {
                const res = await api.get(`productlist/${item.refId}/`);
                const data = res.data?.data;
                if (!data) return item;

                let offerPrice =
                  data.offer_price !== null && data.offer_price !== undefined && Number(data.offer_price) > 0
                    ? Number(data.offer_price)
                    : null;
                let offerPct =
                  data.offer_percentage !== null && data.offer_percentage !== undefined
                    ? Number(data.offer_percentage)
                    : null;

                const parentId = data.parent || data.parent_id;
                if (parentId) {
                  try {
                    const resVar = await api.get(`productvariantfilter/?parent_id=${parentId}`);
                    const resolvedParent = resVar.data?.parent_id;
                    let varList = resVar.data?.data || [];
                    if (resolvedParent && Number(resolvedParent) !== Number(parentId)) {
                      const resVar2 = await api.get(`productvariantfilter/?parent_id=${resolvedParent}`);
                      varList = resVar2.data?.data || varList;
                    }
                    const match = varList.find((v) => Number(v.id) === Number(item.refId));
                    if (match) {
                      offerPrice =
                        match.offer_price !== null && match.offer_price !== undefined && Number(match.offer_price) > 0
                          ? Number(match.offer_price)
                          : null;
                      offerPct =
                        match.offer_percentage !== null && match.offer_percentage !== undefined
                          ? Number(match.offer_percentage)
                          : null;
                    }
                  } catch { }
                }

                // If offer_price equals base price, clear offer fields
                if (offerPrice !== null && offerPrice === Number(data.price)) {
                  offerPrice = null;
                  offerPct = null;
                }

                const newStock = Number(data.stock_quantity ?? data.available_stock ?? item.stock_quantity ?? 0);

                return {
                  ...item,
                  price: Number(data.price),
                  offer_price: offerPrice,
                  offer_percentage: offerPct,
                  stock_quantity: newStock,
                  available_stock: newStock,
                  is_available: data.is_available !== false && newStock > 0,
                };
              } catch (err) {
                return item;
              }
            }
          })
        );

        if (!active) return;

        // Check if any cart item changed
        let hasChanges = false;
        for (let i = 0; i < state.length; i++) {
          const oldItem = state[i];
          const newItem = updatedCart[i];
          if (
            Number(oldItem.price) !== Number(newItem.price) ||
            oldItem.offer_price !== newItem.offer_price ||
            oldItem.offer_percentage !== newItem.offer_percentage ||
            oldItem.is_available !== newItem.is_available ||
            oldItem.stock_quantity !== newItem.stock_quantity
          ) {
            hasChanges = true;
            break;
          }
        }

        if (hasChanges) {
          dispatch(setCart(updatedCart));
        }

        const stockData = {};
        updatedCart.forEach((item) => {
          stockData[item.id] = item.stock_quantity ?? 0;
        });
        setStocks(stockData);
      } catch (error) {
        console.error("Cart sync error:", error);
      }
    };

    if (state.length > 0) {
      syncCartWithLatestData();
    }

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, state.map((i) => `${i.id}-${i.qty}`).join(",")]);

  return (
    <>
      <Navbar />
      <div className="cart-page">
        <div className="container my-3 py-3">
          <h1 className="cart-page-title">
            Cart
          </h1>
          <hr className="border-theme-light" />
          {state.length > 0 ? (
            <ShowCart
              state={state}
              stocks={stocks}
              stockError={stockError}
              disabledQty={disabledQty}
              addItem={addItem}
              removeItem={removeItem}
              formatName={formatName}
              initiateCheckout={initiateCheckout}
              navigate={navigate}
            />
          ) : <EmptyCart />}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Cart;